import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import logo from '../assets/pokeshani-logo.png';
import './CreatorDashboard.css';
import './PaymentTab.css';
import './ProfileModal.css';
import './MarkerterTab.css';
import ProductGrid from './ProductGrid';
import ProductAnalytics from './ProductAnalytics';
import { showToast } from '../utils/toast';
import { format } from 'date-fns';
import { fetchMyPlanStatus, money, type PlanStatus } from '../utils/creatorPlan';
import { FREE_REVENUE_LIMIT } from '../config/plans';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area, Brush, ComposedChart, Bar } from 'recharts';
interface CreatorProfile {
  fullName: string;
  email: string;
  paypalEmail: string | null;
  twitter_url?: string;
  instagram_url?: string;
}

interface DashboardMetrics {
  totalProducts: number;
  totalSales: number;
  totalRevenue: number;
  totalViews: number;
  timeRevenue: {
    last24Hours: number;
    last7Days: number;
    last30Days: number;
    last90Days: number;
  };
  availableForPayout?: number;
  isMarketer?: boolean;
  hasPendingPayout?: boolean;
  revenueData?: Array<{
    name: string;
    revenue: number;
    views: number;
    avg?: number;
  }>;
  revenueDataAll?: Array<{
    name: string;
    revenue: number;
    views: number;
    avg?: number;
  }>;
  salesDistribution?: Array<{
    name: string;
    value: number;
  }>;
}

type Transaction = {
  title: string;
  price: number;
  payment_status: string;
  created_at: string;
  payment_method?: string;
  purchase_type: 'one-time' | 'subscription' | 'free';
};

const CreatorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalProducts: 0,
    totalSales: 0,
    totalRevenue: 0,
    totalViews: 0,
    timeRevenue: {
      last24Hours: 0,
      last7Days: 0,
      last30Days: 0,
      last90Days: 0
    },
    availableForPayout: 0,
    hasPendingPayout: false
  });

  const [selectedTimePeriod, setSelectedTimePeriod] = useState('24h');
  const [chartRange, setChartRange] = useState<'30d' | 'all'>('30d');
  const [isMarketer, setIsMarketer] = useState(false);
  const [selectedAnalyticsProductId, setSelectedAnalyticsProductId] = useState<string | null>(null);
  const [profile, setProfile] = useState<CreatorProfile>({
    fullName: '',
    email: '',
    paypalEmail: null
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(true);
  // Plan + free-plan revenue usage, drives the quota banner.
  const [planStatus, setPlanStatus] = useState<PlanStatus | null>(null);
  const shareOnX = () => {
    const text = 'Loving @pokeshani — zero fees, instant payouts. 🚀';
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    fetchMyPlanStatus().then(setPlanStatus).catch(() => setPlanStatus(null));
  }, []);

  useEffect(() => {
    const fetchTransactions = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: purchaseData, error: purchaseError } = await supabase
        .from('digitalproducts_purchases')
        .select('title, price, payment_status, created_at, payment_method')
        .eq('creator_id', user.id)
        .order('created_at', { ascending: false });

      if (purchaseError) {
        console.error('Error fetching transactions:', purchaseError);
        return;
      }

      const { data: membershipData, error: membershipError } = await supabase
        .from('membership')
        .select('title, price, status, start_date, payment_method, creator_id')
        .eq('creator_id', user.id);

      if (membershipError) {
        console.error('Error fetching membership transactions:', membershipError);
      }

      const mappedPurchases: Transaction[] = (purchaseData || []).map((row: any) => ({
        title: row.title,
        price: row.price,
        payment_status: row.payment_status,
        created_at: row.created_at,
        payment_method: row.payment_method,
        purchase_type:
          row.payment_method === 'Free' || row.price === 0 ? 'free' : 'one-time'
      }));

      const mappedMemberships: Transaction[] = (membershipData || []).map((row: any) => ({
        title: row.title,
        price: row.price,
        payment_status: row.status,
        created_at: row.start_date,
        payment_method: row.payment_method,
        purchase_type: 'subscription'
      }));

      const combined = [...mappedPurchases, ...mappedMemberships].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setTransactions(combined);
    };

    if (activeTab === 'payments') {
      fetchTransactions();
    }
  }, [activeTab]);

  const fetchDashboardData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    // Check if user is a marketer
    if (user?.email) {
      const { data: marketerData } = await supabase
        .from('marketers')
        .select('id')
        .eq('email', user.email)
        .eq('is_active', true)
        .single();
      
      setIsMarketer(!!marketerData);
    }
    if (!user) {
      navigate('/');
      return;
    }

    // Fetch user profile
    const { data: profileData } = await supabase
      .from('profiles')
      .select('full_name, email, paypal_email, twitter_url, instagram_url')
      .eq('id', user.id)
      .single();

    if (profileData) {
      setProfile({
        fullName: profileData.full_name,
        email: profileData.email,
        paypalEmail: profileData.paypal_email,
        twitter_url: profileData.twitter_url,
        instagram_url: profileData.instagram_url
      });
    }

    // Fetch or create metrics from creator_analytics
    try {
      let analyticsData;
      // First try to fetch existing analytics
      const { data: existingAnalytics, error } = await supabase
        .from('creator_analytics')
        .select('total_digitalproducts, total_sales, total_revenue, revenue_24h, revenue_7d, revenue_30d, revenue_90d, available_for_payout')
        .eq('creator_id', user.id)
        .single();

      // Calculate total views across all products
      const { data: productsData, error: productsError } = await supabase
        .from('digitalproducts')
        .select('view_count')
        .eq('creator_id', user.id);

      let totalViews = 0;
      if (productsData) {
        totalViews = productsData.reduce((sum, product) => sum + (product.view_count || 0), 0);
      }

      // If no record exists, create one with default values
      if (error && error.code === 'PGRST116') {
        const defaultAnalytics = {
          creator_id: user.id,
          total_digitalproducts: 0,
          total_sales: 0,
          total_revenue: 0,
          revenue_24h: 0,
          revenue_7d: 0,
          revenue_30d: 0,
          revenue_90d: 0,
          available_for_payout: 0,
          updated_at: new Date().toISOString()
        };

        const { data: newAnalytics, error: insertError } = await supabase
          .from('creator_analytics')
          .upsert(defaultAnalytics)
          .select()
          .single();

        if (insertError) {
          throw insertError;
        }

        analyticsData = newAnalytics;
      } else {
        analyticsData = existingAnalytics;
      }

      // Build last 30 days daily revenue series
      const now = new Date();
      const startDate = new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000);
      const { data: recentPurchases } = await supabase
        .from('digitalproducts_purchases')
        .select('price, created_at, payment_status')
        .eq('creator_id', user.id)
        .eq('payment_status', 'completed')
        .gte('created_at', startDate.toISOString());
      const { data: recentMemberships } = await supabase
        .from('membership')
        .select('price, start_date, status, creator_id')
        .eq('creator_id', user.id)
        .gte('start_date', startDate.toISOString());
      const { data: recentViews, error: viewsError } = await supabase
        .from('product_views')
        .select('viewed_at')
        .gte('viewed_at', startDate.toISOString());

      if (viewsError) {
        console.error('Error fetching recent views:', viewsError);
      }

      const days: string[] = Array.from({ length: 30 }, (_, i) => {
        const d = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
        return format(d, 'MM-dd');
      });
      const dayMap: Record<string, { revenue: number; views: number }> = {};
      days.forEach(k => { dayMap[k] = { revenue: 0, views: 0 }; });
      (recentPurchases || []).forEach(r => {
        const k = format(new Date(r.created_at), 'MM-dd');
        if (dayMap[k] !== undefined) dayMap[k].revenue += r.price || 0;
      });
      (recentMemberships || []).forEach(r => {
        const k = format(new Date(r.start_date), 'MM-dd');
        if (dayMap[k] !== undefined && (r.status === 'completed' || r.status === 'active' || r.status === 'paid')) dayMap[k].revenue += r.price || 0;
      });
      (recentViews || []).forEach(v => {
        const k = format(new Date(v.viewed_at), 'MM-dd');
        if (dayMap[k] !== undefined) dayMap[k].views += 1;
      });

      const series = days.map((k, i) => {
        const rev = dayMap[k].revenue || 0;
        const vws = dayMap[k].views || 0;
        let sum = 0;
        let count = 0;
        for (let j = Math.max(0, i - 6); j <= i; j++) {
          sum += dayMap[days[j]].revenue || 0;
          count++;
        }
        const avg = count > 0 ? sum / count : rev;
        return { 
          name: k, 
          revenue: Number(rev.toFixed(2)), 
          views: vws,
          avg: Number(avg.toFixed(2)) 
        };
      });

      const { data: allPurchases } = await supabase
        .from('digitalproducts_purchases')
        .select('price, created_at, payment_status')
        .eq('creator_id', user.id)
        .eq('payment_status', 'completed');
      const { data: allMemberships } = await supabase
        .from('membership')
        .select('price, start_date, status, creator_id')
        .eq('creator_id', user.id);
      const { data: allViews, error: allViewsError } = await supabase
        .from('product_views')
        .select('viewed_at');

      if (allViewsError) {
        console.error('Error fetching all views:', allViewsError);
      }

      let earliest: Date | null = null;
      (allPurchases || []).forEach(r => {
        const d = new Date(r.created_at);
        if (!earliest || d < earliest) earliest = d;
      });
      (allMemberships || []).forEach(r => {
        const d = new Date(r.start_date);
        if (!earliest || d < earliest) earliest = d;
      });
      (allViews || []).forEach(v => {
        const d = new Date(v.viewed_at);
        if (!earliest || d < earliest) earliest = d;
      });

      const allStart = earliest ? new Date(earliest.getFullYear(), earliest.getMonth(), earliest.getDate()) : startDate;
      const totalDays = Math.max(1, Math.ceil((now.getTime() - allStart.getTime()) / (24 * 60 * 60 * 1000)) + 1);
      const allDays: string[] = Array.from({ length: totalDays }, (_, i) => {
        const d = new Date(allStart.getTime() + i * 24 * 60 * 60 * 1000);
        return format(d, 'yyyy-MM-dd');
      });
      const allDayMap: Record<string, { revenue: number; views: number }> = {};
      allDays.forEach(k => { allDayMap[k] = { revenue: 0, views: 0 }; });
      (allPurchases || []).forEach(r => {
        const k = format(new Date(r.created_at), 'yyyy-MM-dd');
        if (allDayMap[k] !== undefined) allDayMap[k].revenue += r.price || 0;
      });
      (allMemberships || []).forEach(r => {
        const k = format(new Date(r.start_date), 'yyyy-MM-dd');
        if (allDayMap[k] !== undefined && (r.status === 'completed' || r.status === 'active' || r.status === 'paid')) allDayMap[k].revenue += r.price || 0;
      });
      (allViews || []).forEach(v => {
        const k = format(new Date(v.viewed_at), 'yyyy-MM-dd');
        if (allDayMap[k] !== undefined) allDayMap[k].views += 1;
      });

      const seriesAll = allDays.map((k, i) => {
        const rev = allDayMap[k].revenue || 0;
        const vws = allDayMap[k].views || 0;
        let sum = 0;
        let count = 0;
        for (let j = Math.max(0, i - 6); j <= i; j++) {
          sum += allDayMap[allDays[j]].revenue || 0;
          count++;
        }
        const avg = count > 0 ? sum / count : rev;
        return { 
          name: k, 
          revenue: Number(rev.toFixed(2)), 
          views: vws,
          avg: Number(avg.toFixed(2)) 
        };
      });

      // Inside fetchDashboardData function, after fetching analytics
      if (analyticsData) {
        // Check for pending payout request
        const { data: pendingPayout } = await supabase
          .from('payout_requests')
          .select('id')
          .eq('creator_id', user.id)
          .eq('status', 'pending')
          .maybeSingle();
        
        setMetrics({
          totalProducts: analyticsData.total_digitalproducts || 0,
          totalSales: analyticsData.total_sales || 0,
          totalRevenue: analyticsData.total_revenue || 0,
          totalViews: totalViews,
          timeRevenue: {
            last24Hours: analyticsData.revenue_24h || 0,
            last7Days: analyticsData.revenue_7d || 0,
            last30Days: analyticsData.revenue_30d || 0,
            last90Days: analyticsData.revenue_90d || 0
          },
          revenueData: series,
          revenueDataAll: seriesAll,
          availableForPayout: analyticsData.available_for_payout || 0,
          hasPendingPayout: !!pendingPayout
        });
      } else {
        // Set default values if no analytics data is available
        setMetrics({
          totalProducts: 0,
          totalSales: 0,
          totalRevenue: 0,
          totalViews: 0,
          timeRevenue: {
            last24Hours: 0,
            last7Days: 0,
            last30Days: 0,
            last90Days: 0
          },
          revenueData: series,
          revenueDataAll: seriesAll
        });
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setMetrics({
        totalProducts: 0,
        totalSales: 0,
        totalRevenue: 0,
        totalViews: 0,
        timeRevenue: {
          last24Hours: 0,
          last7Days: 0,
          last30Days: 0,
          last90Days: 0
        },
        revenueData: [],
        revenueDataAll: []
      });
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [navigate]);

  const calculateEarnings = (revenue: number = 0, sales: number = 0) => {
    // No platform fees - creators keep 100% of earnings
    return revenue;
  };

  // Ensure we have default values when displaying metrics
  const displayValue = (value: number | undefined) => {
    return (value || 0).toFixed(2);
  };

  // Use the displayValue function when rendering metrics
  const renderMetrics = () => {
    return (
      <div className="metrics-container">
        <div className="metric">
          <h3>Total Revenue</h3>
          <p>${displayValue(metrics.totalRevenue)}</p>
        </div>
        {/* Add other metrics here */}
      </div>
    );
  };

  const handleProfileUpdate = async (updatedProfile: Partial<CreatorProfile>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        showToast.error('Authentication required');
        return;
      }

      // Only update fullName and paypalEmail
      const updates = {
        id: user.id,
        full_name: updatedProfile.fullName || profile.fullName,
        email: user.email, // Keep existing email
        paypal_email: updatedProfile.paypalEmail,
        twitter_url: updatedProfile.twitter_url,
        instagram_url: updatedProfile.instagram_url,
        updated_at: new Date()
      };

      const { error } = await supabase
        .from('profiles')
        .upsert(updates);

      if (error) throw error;

      setProfile(prev => ({
        ...prev,
        fullName: updates.full_name,
        paypalEmail: updates.paypal_email || null
      }));
      setShowProfileModal(false);
      showToast.success('✨ Profile updated successfully!');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      showToast.error(error.message || '❌ Failed to update profile. Please try again.');
    }
  };

  const handlePayoutRequest = async () => {
    try {
      if (!profile) {
        showToast.error('Profile information not available');
        return;
      }

      if (!profile.paypalEmail) {
        showToast.error('Please add your PayPal email in your profile first');
        setShowProfileModal(true);
        return;
      }

      if ((metrics.availableForPayout || 0) < 1) {
        showToast.error('You need at least $1.00 available for payout');
        return;
      }

      if (metrics.hasPendingPayout) {
        showToast.error('You already have a pending payout request');
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        showToast.error('Authentication required');
        return;
      }

      const { error } = await supabase
        .from('payout_requests')
        .insert({
          creator_id: user.id,
          amount: metrics.availableForPayout,
          paypal_email: profile.paypalEmail,
          status: 'pending'
        });

      if (error) throw error;

      setMetrics(prev => ({ ...prev, hasPendingPayout: true }));
      showToast.success('Payout request submitted successfully!');
    } catch (error) {
      console.error('Error requesting payout:', error);
      showToast.error('Failed to submit payout request');
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
      showToast.success('👋 Successfully signed out!');
    } catch (error) {
      console.error('Error signing out:', error);
      showToast.error('❌ Failed to sign out. Please try again.');
    }
  };

  return (
    <div className="dashboard-container">
      
      
      <div className="dashboard-header">
        <div className="logo-container">
          <img src={logo} alt="PokeShani Logo" className="header-logo" />
          <div className="site-name-container">
            <span className="site-name">PokeShani</span>
            <span className="dashboard-label">Dashboard</span>
          </div>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(v => !v)} aria-label="Menu">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <button className="signout-btn" onClick={handleSignOut}>
            <span>Sign Out</span>
          </button>
        </div>
        <div className="header-actions">
          {profile.email === 'pokeshanibusiness@gmail.com' && (
            <button className="profile-btn" onClick={() => navigate('/admin')}>Admin Dashboard</button>
          )}
        </div>
      </div>
      <div className={`sidebar-overlay ${mobileMenuOpen ? 'show' : ''}`} onClick={() => setMobileMenuOpen(false)} />
      {showProfileModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowProfileModal(false);
        }}>
          <div className="profile-modal">
            <h2>Edit Profile</h2>
            <form className="profile-form" onSubmit={(e) => {
              e.preventDefault();
              handleProfileUpdate({
                fullName: e.currentTarget.fullName.value,
                email: e.currentTarget.email.value,
                paypalEmail: e.currentTarget.paypalEmail.value,
                twitter_url: e.currentTarget.twitter_url.value,
                instagram_url: e.currentTarget.instagram_url.value
              });
            }}>
              <div className="form-group">
                <label htmlFor="fullName">Full Name</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  defaultValue={profile.fullName}
                  placeholder="Enter your full name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  defaultValue={profile.email}
                  readOnly
                  style={{ color: '#fff', cursor: 'not-allowed' }}
                />
                <small>
                  To change your email, please send a request to pokeshanibusiness@gmail.com
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="paypalEmail">PayPal Email</label>
                <input
                  type="email"
                  id="paypalEmail"
                  name="paypalEmail"
                  defaultValue={profile.paypalEmail || ''}
                  placeholder="Enter your PayPal email"
                />
              </div>
              <div className="form-group">
                <label htmlFor="twitter_url">Twitter Profile URL</label>
                <input
                  type="url"
                  id="twitter_url"
                  name="twitter_url"
                  placeholder="https://twitter.com/yourusername"
                  defaultValue={profile.twitter_url || ''}
                />
              </div>
              <div className="form-group">
                <label htmlFor="instagram_url">Instagram Profile URL</label>
                <input
                  type="url"
                  id="instagram_url"
                  name="instagram_url"
                  placeholder="https://instagram.com/yourusername"
                  defaultValue={profile.instagram_url || ''}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="modal-btn cancel-btn" onClick={() => setShowProfileModal(false)}>Cancel</button>
                <button type="submit" className="modal-btn save-btn">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="dashboard-layout">
        <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
          <button
            className={`sidebar-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => { setActiveTab('dashboard'); setMobileMenuOpen(false); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
            Dashboard
          </button>
          <button
            className={`sidebar-item ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => { setActiveTab('products'); setMobileMenuOpen(false); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1.11-1.79l-7-3.5a2 2 0 0 0-1.78 0l-7 3.5A2 2 0 0 0 3 8v8a2 2 0 0 0 1.11 1.79l7 3.5a2 2 0 0 0 1.78 0l7-3.5A2 2 0 0 0 21 16z"/><path d="M3.27 7.96L12 12l8.73-4.04"/></svg>
            Products
          </button>
          <button
            className={`sidebar-item ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => { setActiveTab('payments'); setMobileMenuOpen(false); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/><path d="M6 15h4"/></svg>
            Payments
          </button>
          <button
            className={`sidebar-item ${activeTab === 'store' ? 'active' : ''}`}
            onClick={() => { setActiveTab('store'); setMobileMenuOpen(false); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8H3l2-4h14l2 4z"/><path d="M5 8v12h14V8"/><path d="M9 12h6"/></svg>
            Store
          </button>
          <button
            className={`sidebar-item ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => { setActiveTab('analytics'); setMobileMenuOpen(false); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h14"/><path d="M9 12h6"/><path d="M13 19v-4h4"/><path d="M13 13h4"/></svg>
            Analytics
          </button>
          <div className="sidebar-footer">
            <Link to="/pricing" className="sidebar-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4-6.2-4.6-6.2 4.6 2.4-7.4L2 9.4h7.6z"/></svg>
              Plans &amp; Pricing
            </Link>
            <Link to="/faq" className="sidebar-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.82 1c0 2-3 2-3 4"/><path d="M12 17h.01"/></svg>
              FAQ & Help
            </Link>
            <button className="sidebar-link" onClick={() => setShowProfileModal(true)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-3-3.87"/><path d="M4 21v-2a4 4 0 0 1 3-3.87"/><circle cx="12" cy="7" r="4"/></svg>
              Profile
            </button>
            <a
              href="https://x.com/@pokeshani"
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar-link"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 12l2 2 4-4"/></svg>
              Request Help
            </a>
          </div>
        </aside>
        <div className="dashboard-content">
          {!profile.paypalEmail && (
            <div className="paypal-alert">
              <div className="paypal-alert-content">
                <strong>⚠️ Action Required:</strong> Please add your PayPal email in the profile section to receive payments.
              </div>
              <button 
                onClick={() => setShowProfileModal(true)}
                className="paypal-alert-button"
              >
                Update Profile
              </button>
            </div>
          )}

      {activeTab === 'dashboard' ? (
        <>
          {(!planStatus || (!planStatus.isPro && planStatus.limit !== null)) && (
            !planStatus ? (
              <div className="plan-quota-skeleton" aria-hidden="true">
                Loading your plan usage...
              </div>
            ) : (
            <div
              className={`plan-quota-card ${planStatus.isLimitReached ? 'is-capped' : ''} ${!planStatus.isLimitReached && planStatus.percentUsed >= 80 ? 'is-warning' : ''}`}
            >
              <div className="plan-quota-head">
                <div>
                  <span className="plan-quota-label">
                    {planStatus.isLimitReached ? 'Free plan limit reached' : 'Free plan revenue'}
                  </span>
                  <span className="plan-quota-values">
                    {money(planStatus.totalRevenue)}{' '}
                    <small>of {money(planStatus.limit)} used</small>
                    <span className="plan-quota-pct">
                      {Math.round(planStatus.percentUsed)}%
                    </span>
                  </span>
                </div>
                <button className="plan-quota-upgrade" onClick={() => navigate('/pricing')}>
                  {planStatus.isLimitReached ? 'Upgrade to restore checkout' : 'Upgrade to Pro'}
                </button>
              </div>
              <div className="plan-quota-meter">
                <div
                  className={`plan-quota-fill ${planStatus.isLimitReached ? 'full' : ''} ${!planStatus.isLimitReached && planStatus.percentUsed >= 80 ? 'is-warning' : ''}`}
                  style={{ width: `${planStatus.percentUsed}%` }}
                />
              </div>
              <div className="plan-quota-scale">
                <span>$0</span>
                <span>{money(planStatus.limit)}</span>
              </div>
              <p className="plan-quota-note">
                {planStatus.isLimitReached
                  ? <>Checkout is <strong>hidden</strong> on your sales pages until you upgrade. Your pages stay live.</>
                  : planStatus.percentUsed >= 80
                  ? <>You&apos;re almost at the limit — <strong>{planStatus.remaining !== null ? money(planStatus.remaining) : ''} left</strong>. Upgrade now so checkout never pauses.</>
                  : planStatus.remaining !== null
                  ? <><strong>{money(planStatus.remaining)} left</strong> on your free plan. Upgrade to sell without a cap.</>
                  : 'Upgrade to remove this limit.'}
              </p>
            </div>
            )
          )}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-title">Total Products</div>
              <div className="metric-value">{metrics.totalProducts}</div>
            </div>
            <div className="metric-card">
              <div className="metric-title">Total Sales</div>
              <div className="metric-value">{metrics.totalSales}</div>
            </div>
            <div className="metric-card">
              <div className="metric-title">Total Product's Views</div>
              <div className="metric-value">{metrics.totalViews}</div>
            </div>
            <div className="metric-card">
              <div className="metric-title">Total Revenue</div>
              <div className="metric-value">${metrics.totalRevenue.toFixed(2)}</div>
            </div>
          </div>

          <div className="time-revenue-container">
            <div className="revenue-display">
              <div>
                <select
                  className="time-select"
                  value={selectedTimePeriod}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedTimePeriod(value);
                  }}
                >
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                </select>
                <div className="metric-value">
                  ${(selectedTimePeriod === '24h' ? metrics.timeRevenue.last24Hours :
                     selectedTimePeriod === '7d' ? metrics.timeRevenue.last7Days :
                     selectedTimePeriod === '30d' ? metrics.timeRevenue.last30Days :
                     metrics.timeRevenue.last90Days).toFixed(2)}
                </div>
                <div className="metric-label">Revenue</div>
              </div>
            </div>
          </div>

          <div className="action-section">
            <button className="create-product-btn" onClick={() => navigate('/marketplace')}>Marketplace</button>
            <button className="create-product-btn" onClick={() => navigate('/create')}>Sell your Digital Products</button>
            {isMarketer && (
              <button className="create-product-btn marketer-btn" onClick={() => navigate('/marketer-dashboard')}>
                Marketer Dashboard
              </button>
            )}
          </div>
          <div className="dashboard-charts">
            <div className="chart-container revenue-chart">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3>Revenue & Views Trend</h3>
                <select
                  className="time-select"
                  value={chartRange}
                  onChange={(e) => setChartRange(e.target.value as '30d' | 'all')}
                  style={{ maxWidth: 160 }}
                >
                  <option value="30d">Last 30 Days</option>
                  <option value="all">All Time</option>
                </select>
              </div>
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={(chartRange === 'all' ? (metrics.revenueDataAll || []) : (metrics.revenueData || []))} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
                  {/* Dashed horizontal gridlines only, like the reference chart */}
                  <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="rgba(255,255,255,0.08)" />
                  <defs>
                    <linearGradient id="dashboardRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4285f4" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#4285f4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="name" 
                    stroke="#9ca3af" 
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    dy={10}
                  />
                  <YAxis 
                    yAxisId="left" 
                    stroke="#4285f4" 
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => {
                      if (value === 0) return '$0';
                      if (value >= 1000) {
                        const k = value / 1000;
                        return `$${Number.isInteger(k) ? k : k.toFixed(1)}K`;
                      }
                      return `$${value}`;
                    }}
                    label={{ value: 'Revenue', angle: -90, position: 'insideLeft', fill: '#4285f4', fontSize: 12, offset: 10 }}
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right" 
                    stroke="#9334e1" 
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, 'auto']} 
                    allowDecimals={false}
                    label={{ value: 'Views', angle: 90, position: 'insideRight', fill: '#9334e1', fontSize: 12, offset: 10 }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'rgba(26, 28, 44, 0.98)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: '12px',
                      color: '#fff',
                      fontSize: '15px',
                      padding: '12px 16px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                      fontWeight: 500
                    }}
                    itemStyle={{ padding: '4px 0' }}
                    labelStyle={{ marginBottom: '8px', fontSize: '16px', fontWeight: 700, color: '#9ca3af' }}
                    formatter={(value: number, name: string) => [
                      name === 'Views' ? value : `$${Number(value).toFixed(2)}`, 
                      name
                    ]}
                  />
                  <Legend verticalAlign="top" align="right" height={36} iconType="circle" />
                  <Area yAxisId="left" type="linear" dataKey="revenue" stroke="#4285f4" strokeWidth={2} fill="url(#dashboardRevenueGradient)" fillOpacity={1} dot={{ r: 3, strokeWidth: 0, fill: '#4285f4' }} activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2, fill: '#4285f4' }} name="Revenue" />
                  <Line yAxisId="right" type="linear" dataKey="views" stroke="#9334e1" strokeWidth={2} dot={{ r: 3, strokeWidth: 0, fill: '#9334e1' }} activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2, fill: '#9334e1' }} name="Views" />
                  <Brush
                    dataKey="name"
                    height={30}
                    travellerWidth={10}
                    stroke="#9ca3af"
                    fill="rgba(255,255,255,0.05)"
                    strokeWidth={1}
                  />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="chart-tip">
                <span><strong>Tip:</strong> Drag the handles at the bottom left and right to zoom into specific time periods.</span>
              </div>
            </div>

            <div className="chart-container pie-chart">
              <h3>Sales Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Sales', value: metrics.totalSales, fill: '#4f46e5' },
                      { name: 'Products', value: metrics.totalProducts, fill: '#6366f1' },
                      { name: 'Revenue', value: metrics.totalRevenue, fill: '#8b5cf6' },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={true}
                  >
                    {[
                      { name: 'Total Products', color: '#6366f1' },
                      { name: 'Total Sales', color: '#4f46e5' },
                      { name: 'Revenue', color: '#8b5cf6' },
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value) => `${value} (${value === 'Total Products' ? 'Purple' : value === 'Total Sales' ? 'Blue' : 'Violet'})`}
                  />
              <Tooltip
                contentStyle={{
                  background: 'rgba(26, 28, 44, 0.95)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '1rem',
                  padding: '0.75rem 1rem'
                }}
                itemStyle={{ color: '#fff', fontSize: '0.95rem', fontWeight: 600 }}
                labelStyle={{ color: '#fff', fontWeight: 700 }}
                formatter={(value: number, name: string) => {
                  const total = (metrics.totalSales || 0) + (metrics.totalProducts || 0) + (metrics.totalRevenue || 0);
                  const numeric = typeof value === 'number' ? value : Number(value);
                  const pct = total > 0 ? ((numeric / total) * 100).toFixed(0) : '0';
                  const val = name === 'Revenue' ? `$${numeric.toFixed(2)}` : numeric;
                  return [`${val} (${pct}%)`, name];
                }}
              />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          
        </>
      ) : activeTab === 'store' ? (
        <div className="store-tab">
          <div className="referral-section" style={{
            background: 'rgba(255, 255, 255, 0.1)',
            padding: '1.5rem',
            borderRadius: '0.5rem',
            marginBottom: '2rem'
          }}>
            <h3 style={{ color: '#fff', marginBottom: '1rem' }}>🎯 Grow Your Store</h3>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  const storeUrl = `${window.location.origin}/store/${profile.email.split('@')[0]}`;
                  navigator.clipboard.writeText(storeUrl);
                  showToast.success('Store link copied!');
                }}
                style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                color: '#fff',
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                transform: 'translateY(0)',
                boxShadow: '0 2px 4px rgba(99, 102, 241, 0.2)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(99, 102, 241, 0.2)';
              }}
              >
                🔗 Copy Store Link
              </button>
              <button
                onClick={() => {
                  const text = `Check out my digital products store on PokeShani! ${window.location.origin}/store/${profile.email.split('@')[0]}`;
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
                }}
                style={{
                background: 'linear-gradient(135deg, #1DA1F2 0%, #0d8bd9 100%)',
                color: '#fff',
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                transform: 'translateY(0)',
                boxShadow: '0 2px 4px rgba(29, 161, 242, 0.2)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(29, 161, 242, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(29, 161, 242, 0.2)';
              }}
              >
                🐦 Share on Twitter
              </button>
            </div>
          </div>
          <div className="social-media-section" style={{
            background: 'rgba(255, 255, 255, 0.1)',
            padding: '1.5rem',
            borderRadius: '0.5rem',
            marginBottom: '2rem'
          }}>
            <h3 style={{ color: '#fff', marginBottom: '1rem' }}>🌐 Social Media Links</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {profile.twitter_url || profile.instagram_url ? (
                <>
                  {profile.twitter_url && (
                    <a
                      href={profile.twitter_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: '#1DA1F2',
                        textDecoration: 'none',
                        fontSize: '1rem'
                      }}
                    >
                      🐦 Twitter Profile
                    </a>
                  )}
                  {profile.instagram_url && (
                    <a
                      href={profile.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: '#E1306C',
                        textDecoration: 'none',
                        fontSize: '1rem'
                      }}
                    >
                      📸 Instagram Profile
                    </a>
                  )}
                </>
              ) : (
                <p style={{ color: '#9ca3af' }}>
                  No social media links added yet. Add them through your profile settings to connect with your audience!
                </p>
              )}
              <button
                onClick={() => setShowProfileModal(true)}
                style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  color: '#fff',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: 'pointer',
                  marginTop: '1rem',
                  width: 'fit-content',
                  transition: 'all 0.3s ease',
                  transform: 'translateY(0)',
                  boxShadow: '0 2px 4px rgba(99, 102, 241, 0.2)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(99, 102, 241, 0.2)';
                }}
              >
                ✏️ Edit Social Links
              </button>
            </div>
          </div>

          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center', 
            marginBottom: '2rem'
          }}>
            <div>
              <h2 style={{ color: '#fff', marginBottom: '0.5rem' }}>{profile.fullName}'s Store</h2>
              <p style={{ color: '#9ca3af' }}>Digital Products Creator</p>
            </div>
            <button
              onClick={() => {
                const storeUrl = window.location.origin + '/store/' + profile.email.split('@')[0];
                navigator.clipboard.writeText(storeUrl);
                showToast.success('Store link copied to clipboard!');
              }}
              style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                color: '#fff',
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                transition: 'all 0.3s ease',
                transform: 'translateY(0)',
                boxShadow: '0 2px 4px rgba(99, 102, 241, 0.2)',
                zIndex: 10
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(99, 102, 241, 0.2)';
              }}
            >
              🔗 Share Store
            </button>
          </div>
          <div style={{ marginBottom: '2rem' }}>
            <ProductGrid 
              onAnalyticsClick={(productId) => { setSelectedAnalyticsProductId(productId); setActiveTab('analytics'); }} 
              onProductUpdate={fetchDashboardData}
            />
          </div>
        </div>
      ) : activeTab === 'products' ? (
        <div className="products-tab">
          <h2>Your Digital Products</h2>
          <div className="action-section" style={{ justifyContent: 'flex-start' }}>
            <button className="create-product-btn" onClick={() => navigate('/create')}>Sell your Digital Product</button>
            <button className="create-product-btn" onClick={() => navigate('/marketplace')}>Marketplace</button>
          </div>
          <ProductGrid 
            onAnalyticsClick={(productId) => { setSelectedAnalyticsProductId(productId); setActiveTab('analytics'); }} 
            onProductUpdate={fetchDashboardData}
          />
        </div>
      ) : activeTab === 'analytics' ? (
        <div className="analytics-tab">
          <div className="analytics-header">
            <h2>Product's Performance</h2>
          </div>
          <div className="analytics-content">
            <ProductAnalytics initialProductId={selectedAnalyticsProductId} />
          </div>
        </div>
      ) : (
        <div className="payment-tab">
          <div className="payment-header">
            <h2>Transaction History</h2>
          </div>
          <div className="payment-form">
            <div className="transactions-table">
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Price</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction, index) => (
                    <tr key={index}>
                      <td>{transaction.title}</td>
                      <td>${transaction.price.toFixed(2)}</td>
                      <td>
                        {transaction.purchase_type === 'subscription'
                          ? 'Subscription'
                          : transaction.purchase_type === 'free'
                          ? 'Free'
                          : 'One-Time'}
                      </td>
                      <td>
                        <span className={`status ${transaction.payment_status.toLowerCase()}`}>
                          {transaction.payment_status}
                        </span>
                      </td>
                      <td>{format(new Date(transaction.created_at), 'MMM dd, yyyy HH:mm')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    {showFeedbackPopup && (
      <div className="feedback-popup">
        <div className="feedback-text">Enjoying PokeShani?</div>
        <div className="feedback-actions">
          <button className="feedback-tweet-btn" onClick={shareOnX}>Share on X</button>
          <button className="feedback-close-btn" onClick={() => setShowFeedbackPopup(false)}>✕</button>
        </div>
      </div>
    )}
  </div>
  );
};

export default CreatorDashboard;
